import type {
    ApplyOptions,
    ReplayService,
    ReplayOptions,
    ReplayPreparation,
    ReplayReport,
} from "../ReplayService.js";
import { assertActive, computeCommitBuckets, dedupeDegradedReasons } from "../ReplayService.js";
import type { ReplayResult } from "../types.js";
import type { Flow } from "./Flow.js";

/**
 * Lockfile exists with zero tracked patches. Only newly detected patches
 * apply. Simpler than NormalRegeneration because there's no pre-rebase work
 * (no existing patches to refresh).
 */
export class NoPatchesFlow implements Flow {
    constructor(private readonly service: ReplayService) {}

    async prepare(options?: ReplayOptions): Promise<ReplayPreparation> {
        const preLock = this.service.lockManager.read();
        const previousGenerationSha = preLock.current_generation ?? null;

        let { patches: newPatches, revertedPatchIds } = await this.service.detector.detectNewPatches();
        // Snapshot of warnings available at detection time. Re-merged with
        // service-level warnings (populated during rebase) when the final report
        // is built in phase 2.
        const detectorWarnings = [...this.service.detector.warnings];
        const detectorDegraded = [...this.service.detector.degradedReasons];

        if (options?.dryRun) {
            const dryRunWarnings = [...detectorWarnings, ...this.service.warnings];
            const dryRunDegraded = dedupeDegradedReasons([...detectorDegraded, ...this.service.degradedReasons]);
            const preparedReport: ReplayReport = {
                flow: "no-patches",
                patchesDetected: newPatches.length,
                patchesApplied: 0,
                patchesWithConflicts: 0,
                patchesSkipped: 0,
                patchesReverted: revertedPatchIds.length,
                conflicts: [],
                wouldApply: newPatches,
                warnings: dryRunWarnings.length > 0 ? dryRunWarnings : undefined,
                degraded: dryRunDegraded.length > 0 ? true : undefined,
                degradedReasons: dryRunDegraded.length > 0 ? dryRunDegraded : undefined,
            };
            const headSha = await this.service.git
                .exec(["rev-parse", "HEAD"])
                .then((s) => s.trim())
                .catch(() => "");
            return {
                flow: "no-patches",
                previousGenerationSha,
                currentGenerationSha: headSha,
                _prepared: {
                    kind: "terminal",
                    flow: "no-patches",
                    preparedReport,
                },
            };
        }

        // Pre-apply absorption: drop patches whose net customer effect (from
        // currentGen → HEAD) on all of their files is empty. This mirrors the
        // empty-diff branch of preGenerationRebase which runs only on the
        // normal-regeneration path — without this, flows diverge when the
        // applicator cannot fully cancel a create+delete sequence (parity
        // with normal-regen's revert-detection path).
        {
            const preCurrentGen = preLock.current_generation;
            const uniqueFiles = [...new Set(newPatches.flatMap((p) => p.files))];
            const absorbedFiles = new Set<string>();
            for (const file of uniqueFiles) {
                const diff = await this.service.git
                    .exec(["diff", preCurrentGen, "HEAD", "--", file])
                    .catch(() => null);
                if (diff !== null && !diff.trim()) {
                    absorbedFiles.add(file);
                }
            }
            if (absorbedFiles.size > 0) {
                newPatches = newPatches.filter(
                    (p) => p.files.length === 0 || !p.files.every((f) => absorbedFiles.has(f))
                );
            }
        }

        // Remove reverted patches from lockfile (after dry-run check to avoid mutating state)
        for (const id of revertedPatchIds) {
            try {
                this.service.lockManager.removePatch(id);
            } catch {
                // patch may not be in lockfile
            }
        }

        const commitOpts = options
            ? {
                  cliVersion: options.cliVersion ?? "unknown",
                  generatorVersions: options.generatorVersions ?? {},
              }
            : undefined;

        await this.service.committer.commitGeneration("Update SDK", commitOpts);

        // Clean up stale conflict markers from a previous crashed run.
        await this.service.cleanupStaleConflictMarkers();

        const genRecord = await this.service.committer.createGenerationRecord(commitOpts);

        // Add generation to lockfile BEFORE applying patches so the applicator
        // can read the current generation's tree hash for rename detection.
        this.service.lockManager.addGeneration(genRecord);

        return {
            flow: "no-patches",
            previousGenerationSha,
            currentGenerationSha: genRecord.commit_sha,
            _prepared: {
                kind: "active",
                flow: "no-patches",
                patchesToApply: newPatches,
                newPatchIds: new Set(newPatches.map((p) => p.id)),
                detectorWarnings,
                detectorDegraded,
                revertedCount: revertedPatchIds.length,
                genSha: genRecord.commit_sha,
                optionsSnapshot: options,
            },
        };
    }

    async apply(prep: ReplayPreparation, options?: ApplyOptions): Promise<ReplayReport> {
        assertActive(prep);
        const newPatches = prep._prepared.patchesToApply;
        const detectorWarnings = prep._prepared.detectorWarnings;
        const genSha = prep._prepared.genSha;

        let results: ReplayResult[] = [];
        if (newPatches.length > 0) {
            results = await this.service.applicator.applyPatches(newPatches);
            this.service._lastApplyResults = results;

            // Surface "dropped context line" warnings — see NormalRegenerationFlow.
            this.service.recordDroppedContextLineWarnings(results);

            // Surface "unreachable base" warnings (INC-866) — see NormalRegenerationFlow.
            this.service.recordUnreachableBaseWarnings(results);

            // Strip conflict markers from conflicting files so the commit is clean.
            this.service.revertConflictingFiles(results);

            // Mark conflict patches as unresolved on the patch objects before adding to lockfile.
            for (const result of results) {
                if (result.status === "conflict") {
                    result.patch.status = "unresolved";
                }
            }
        }

        // Rebase cleanly applied patches to the current generation.
        // This prevents recurring conflicts on subsequent regenerations.
        const rebaseCounts = await this.service.rebasePatches(results, genSha);

        // Save lockfile BEFORE commit — lockfile is source of truth.
        for (const patch of newPatches) {
            if (!rebaseCounts.absorbedPatchIds.has(patch.id)) {
                this.service.lockManager.addPatch(patch);
            }
        }
        this.service.lockManager.setFernignorePatterns(this.service.readFernignorePatterns());
        this.service.lockManager.save();
        this.service.warnings.push(...this.service.lockManager.warnings);

        // Preserve today's L422 guard: when no new patches were detected, neither
        // commitReplay nor stageAll runs. The in-memory `addGeneration` still
        // happened in phase 1; the save just above persisted it.
        if (newPatches.length > 0) {
            if (!options?.stageOnly) {
                // Count only cleanly applied patches for the commit message.
                const appliedCount = results.filter((r) => r.status === "applied").length;
                const buckets = computeCommitBuckets(results, rebaseCounts.absorbedPatchIds, newPatches);
                await this.service.committer.commitReplay(appliedCount, newPatches, undefined, { buckets });
            } else {
                await this.service.committer.stageAll();
            }
        }

        const warnings = [...detectorWarnings, ...this.service.warnings];
        const degradedReasons = [...prep._prepared.detectorDegraded, ...this.service.degradedReasons];
        return this.service.buildReport(
            "no-patches",
            newPatches,
            results,
            prep._prepared.optionsSnapshot,
            warnings,
            rebaseCounts,
            undefined,
            prep._prepared.revertedCount,
            degradedReasons,
        );
    }
}
